<template>
  <div class="tw-bg-white dark:tw-bg-dm tw-rounded-2xl">
    <div
      v-if="!isEditing"
      class="tw-p-4"
    >
      <div class="tw-flex tw-items-center tw-justify-between tw-w-full">
        <div class="tw-flex tw-flex-col tw-gap-2">
          <div class="tw-flex tw-items-center tw-justify-between">
            <div class="tw-inline-flex tw-text-base tw-leading-5">
              <span class="tw-font-medium tw-tracking-wider">
                {{ actionDate.action_date_type_name }}
              </span>
              <span v-if="!actionDate.action_date || !actionDate.action_time">
                {{ actionDate.action_date }}
                <span v-if="actionDate.action_time">
                  {{ $t('common.prepositions.at') }} {{ actionDate.action_time }}
                </span>
              </span>
              <div
                v-else
                class="tw-flex tw-items-center tw-ml-0.5"
                @click.prevent="downloadIcs"
              >
                <span class="tw-cursor-pointer hover:tw-underline">
                  {{ actionDate.action_date }}
                  {{ $t('common.prepositions.at') }} {{ actionDate.action_time }}
                </span>
                <icon-download class="tw-ml-1" />
              </div>
            </div>
          </div>
          <div class="tw-flex tw-items-center tw-text-base tw-leading-6 tw-tracking-normal">
            <div
              v-if="actionDate.place_full_address"
              class="tw-flex tw-items-center tw-mr-4"
            >
              <icon-pin-sm class="tw-w-4 tw-mr-2" />
              <span>{{ actionDate.place_full_address }}</span>
            </div>
            <div class="tw-tracking-normal">
              <!-- TODO: add avatar -->
              {{ actionDate.assistance_fullname }}
            </div>
          </div>
          <div
            v-if="actionDate.note"
            class="tw-text-gray-dark dark:tw-text-dm-gray-dark tw-flex tw-items-center tw-text-base tw-leading-5 tw-tracking-normal"
          >
            <icon-screen-list class="tw-w-4 tw-mr-2" />
            {{ actionDate.note }}
          </div>
        </div>

        <div class="tw-flex tw-items-center tw-gap-3">
          <div class="tw-flex tw-items-center tw-gap-1">
            <icon-f
              class="tw-w-4"
              :class="actionDate.share_with_foreigner ? 'tw-text-dark dark:tw-text-dm-dark' : 'tw-text-gray dark:tw-text-dm-gray'"
            />
            <icon-c
              class="tw-w-4"
              :class="actionDate.share_with_client ? 'tw-text-dark dark:tw-text-dm-dark' : 'tw-text-gray dark:tw-text-dm-gray'"
            />
          </div>
          <i-button
            v-if="!isObtained"
            type="default"
            is-icon
            @click.stop="isEditing = true"
          >
            <icon-edit />
          </i-button>
        </div>
      </div>
    </div>
    <div
      v-else
      class="tw-p-4"
    >
      <el-form
        ref="Form"
        :model="form"
        label-position="top"
        :disabled="isLoading"
        class="tw-flex tw-flex-col tw-gap-3"
        @submit.native.prevent="() => {}"
      >
        <div class="tw-flex tw-items-center tw-justify-between tw-mb-3">
          <div>
            <span class="tw-font-medium tw-tracking-wider">
              {{ actionDate.action_date_type_name }}
            </span>
          </div>
          <div class="tw-flex tw-items-center tw-gap-1">
            <el-date-picker
              v-model="form.action_date"
              :disabled="isObtained"
              type="date"
              format="DD.MM.YYYY"
              value-format="DD.MM.YYYY"
              class="small tw-w-[128px]"
              :placeholder="$t('common.fields.date')"
            />

            <el-time-picker
              v-model="form.action_time"
              :disabled="isObtained"
              class="small tw-w-[87px]"
              value-format="HH:mm"
              format="HH:mm"
              :placeholder="$t('common.fields.time')"
            />
          </div>
        </div>
        <div class="tw-flex tw-gap-3">
          <el-form-item
            :label="$t('common.fields.place')"
            prop="place_id"
          >
            <el-select
              v-model="form.place_id"
              :disabled="isObtained"
              placeholder=""
              filterable
              :filter-method="placesFilterMethod"
              :visible-change="handlePlacesVisibleChange"
            >
              <el-option
                v-for="item in filteredPlaces"
                :key="item.id"
                :label="item.short_name"
                :value="item.id"
              />
            </el-select>
          </el-form-item>
          <el-form-item
            :label="$t('foreigners.permits.assistance')"
            prop="assistance_id"
          >
            <el-select
              v-model="form.assistance_id"
              :disabled="isObtained || !$isInRole(['ADMIN', 'CONSULTANT'])"
              placeholder=""
              filterable
              :filter-method="internalUsersFilterMethod"
              :visible-change="handleInternalUsersVisibleChange"
            >
              <el-option
                v-for="item in filteredInternalUsers"
                :key="item.id"
                :label="item.fullname"
                :value="item.id"
              />
            </el-select>
          </el-form-item>
        </div>
        <el-form-item
          :label="$t('foreigners.permits.note')"
          prop="note"
        >
          <el-input
            v-model="form.note"
            :disabled="isObtained"
            type="textarea"
            :rows="2"
          />
        </el-form-item>
        <div class="tw-flex tw-items-center tw-justify-between">
          <el-switch
            v-model="form.share_with_foreigner"
            :disabled="isObtained"
            :active-text="$t('foreigners.permits.share_date')"
          />
          <i-button
            type="default"
            @click.stop="isEditing = false"
          >
            {{ $t('common.actions.close') }}
          </i-button>
        </div>
      </el-form>
    </div>
  </div>
</template>

<script>
import { internalUsersFilterMixin } from '@/mixins/internalUsersFilterMixin';
import { placesFilterMixin } from '@/mixins/placesFilterMixin';
import { mapGetters, mapState } from 'vuex';
import { downloadFileByUrlV2 } from '@/helpers';
import eventBus from '../../plugins/eventBus';

export default {
  mixins: [internalUsersFilterMixin, placesFilterMixin],
  props: {
    actionDate: {
      type: Object,
      required: true,
    },
    permit: {
      type: Object,
      required: true,
    },
    isObtained: {
      type: Boolean,
      default: false,
    },
    statusId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      isEditing: false,
      isLoading: false,

      form: {},
    };
  },

  computed: {
    ...mapState({
      foreigner: (state) => state.v2.admin.foreigners.foreigner.foreigner,
    }),
    ...mapGetters({
      rawPlacesSelectOptions: 'v2/admin/cms/internal/places/selectOptions',
      internalUsersRaw: 'v2/admin/cms/internal/users/selectOptions',
    }),
    internalUsers() {
      return [
        { fullname: ' ', id: null },
        { fullname: this.$t('foreigners.permits.assistance.without'), id: -2 },
        ...this.internalUsersRaw,
      ];
    },
    places() {
      return [{ short_name: ' ', id: null }, ...this.rawPlacesSelectOptions];
    },
  },

  watch: {
    form: {
      async handler(form, oldForm) {
        if (Object.keys(oldForm).length) {
          this.$emit('update', form);
        }
      },
      deep: true,
    },
  },

  mounted() {
    this.form = { ...this.actionDate };
    eventBus.on('save', this.handleSave);
  },
  beforeUnmount() {
    eventBus.off('save', this.handleSave);
  },
  methods: {
    downloadIcs() {
      downloadFileByUrlV2(`api/foreigner/${this.foreigner.id}/permit/${this.permit.id}/status/${this.statusId}/${this.actionDate.id}/ics/`, '', this.$axios);
    },
    handleSave(permitStatusId) {
      if (permitStatusId === this.statusId) {
        this.isEditing = false;
      }
    },
  },
};
</script>
